import { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, Contact as ContactIcon, Building2, Search, Radio, Linkedin, RotateCcw, Trash2 } from "lucide-react";
import { HiringContactsProvider, useHiringContacts } from "./modal/HiringContactsContext";
import { STATUS_OPTIONS, COMPANY_STATUS_OPTIONS, fetchAssignmentDetail, fetchCompanyAssignmentDetail, fetchContacts } from "./api/HiringContactsApi";
import type { HiringContact, Company } from "./api/HiringContactsApi";
import ContactCard from "./ui/ContactCard";
import ContactDetailModal from "./ui/ContactDetailModal";
import CompanyAssignmentCard from "./ui/CompanyAssignmentCard";
import CompanyAssignmentDetailModal from "./ui/CompanyAssignmentDetailModal";
import CompanyTable from "./ui/CompanyTable";
import AssignContactModal from "./ui/AssignContactModal";
import AssignCompanyModal from "./ui/AssignCompanyModal";
import CreateContactModal from "./ui/CreateContactModal";
import CreateCompanyModal from "./ui/CreateCompanyModal";
import AppSelect from "shared/AppSelect";
import { Plus } from "lucide-react";
import { WatchlistProvider } from "features/watchlist/modal/WatchlistContext";
import { RawPostsTab } from "features/watchlist/WatchlistPage";
import ConfirmModal from "features/watchlist/ui/ConfirmModal";

const FILTER_STATUS_OPTIONS = [{ value: "", label: "All" }, ...STATUS_OPTIONS];
const FILTER_COMPANY_STATUS_OPTIONS = [{ value: "", label: "All" }, ...COMPANY_STATUS_OPTIONS];
const WATCH_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Watched" },
  { value: "false", label: "Not Watched" },
];

interface Props {
  isAdmin: boolean;
}

export default function HiringContactsPage({ isAdmin }: Props) {
  return (
    <HiringContactsProvider isAdmin={isAdmin}>
      <HiringContactsBody />
    </HiringContactsProvider>
  );
}

function HiringContactsBody() {
  const { isAdmin, assignableEmployees, selectedManagerId, setSelectedManagerId } = useHiringContacts();
  const [tab, setTab] = useState<"assignments" | "company_assignments" | "contacts" | "companies" | "watchlist">(
    isAdmin ? "companies" : "assignments"
  );

  const tabs: { id: "assignments" | "company_assignments" | "contacts" | "companies" | "watchlist"; label: string; icon: React.ReactNode }[] = isAdmin
    ? [
        { id: "companies", label: "Companies", icon: <Building2 className="w-4 h-4" /> },
        { id: "contacts", label: "Contacts", icon: <ContactIcon className="w-4 h-4" /> },
        { id: "watchlist", label: "Watchlist Posts", icon: <Radio className="w-4 h-4" /> },
      ]
    : [
        { id: "assignments", label: "My Contacts", icon: <ContactIcon className="w-4 h-4" /> },
        { id: "company_assignments", label: "My Companies", icon: <Building2 className="w-4 h-4" /> },
      ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">LinkedIn Hiring Contacts</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            {isAdmin
              ? "Track company engagement and outreach to individual contacts, or browse companies to see their hiring team."
              : "Your assigned hiring contacts and companies — connect, engage, and follow up on LinkedIn."}
          </p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Employee:</label>
            <AppSelect
              value={selectedManagerId?.toString() ?? ""}
              onChange={(v) => setSelectedManagerId(v ? parseInt(v, 10) : null)}
              className="w-48"
            >
              <option value="">Everyone</option>
              {assignableEmployees.map((e) => (
                <option key={e.id} value={e.id.toString()}>{e.name}</option>
              ))}
            </AppSelect>
          </div>
        )}
      </div>

      {isAdmin && assignableEmployees.length === 0 && (
        <div className="text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
          No roles currently have the "LinkedIn Hiring Contacts" permission granted, so contacts can't be assigned to anyone yet.
          Grant it to a role in Roles Management.
        </div>
      )}

      {tabs.length > 1 && (
        <div className="flex items-end gap-1 border-b border-border pb-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-inter rounded-t-lg transition-colors -mb-px border-b-2 ${
                tab === t.id
                  ? "border-primary text-foreground bg-primary/10"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === "assignments" && <AssignmentsTab />}
      {tab === "company_assignments" && !isAdmin && <CompanyAssignmentsTab />}
      {tab === "contacts" && isAdmin && <ContactsMergedTab />}
      {tab === "companies" && isAdmin && <CompaniesMergedTab />}
      {tab === "watchlist" && isAdmin && <WatchlistPostsTab />}
    </div>
  );
}

function WatchlistPostsTab() {
  return (
    <WatchlistProvider>
      <RawPostsTab />
    </WatchlistProvider>
  );
}

/** Merges what used to be two separate tabs ("Assigned Contacts" / "All Contacts")
 * into one destination with a scope toggle — both were "contacts", just filtered
 * differently, and living in separate tabs made it unclear where to look. */
function ContactsMergedTab() {
  const [scope, setScope] = useState<"assigned" | "unassigned" | "archived">("assigned");
  const { contactsWatchFilter, setContactsWatchFilter, statusCounts, unassignedCount, archivedContactsCount, contactActionError } = useHiringContacts();

  const assignedCount = Object.values(statusCounts).reduce((sum, n) => sum + n, 0);

  return (
    <div className="space-y-4">
      {contactActionError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {contactActionError}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 p-1 bg-surface/60 border border-border rounded-xl">
          <button
            onClick={() => setScope("assigned")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "assigned" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Assigned
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "assigned" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {assignedCount}
            </span>
          </button>
          <button
            onClick={() => setScope("unassigned")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "unassigned" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Unassigned
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "unassigned" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {unassignedCount}
            </span>
          </button>
          <button
            onClick={() => setScope("archived")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "archived" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Archived
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "archived" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {archivedContactsCount}
            </span>
          </button>
        </div>

        {scope !== "archived" && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Watch:</label>
            <AppSelect value={contactsWatchFilter} onChange={setContactsWatchFilter} className="w-36">
              {WATCH_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </AppSelect>
          </div>
        )}
      </div>

      {scope === "assigned" ? <AssignmentsTab /> : scope === "unassigned" ? <UnassignedContactsTab /> : <ArchivedContactsTab />}
    </div>
  );
}

function AssignmentsTab() {
  const {
    isAdmin, assignments, assignmentsTotalPages, assignmentsPage,
    assignmentsLoading, assignmentsLoadError, statusFilter, statusCounts,
    setStatusFilter, loadAssignments,
  } = useHiringContacts();

  // Store just the id, not the object — deriving it fresh from `assignments` on every
  // render means the open modal picks up watch-toggle/status updates immediately
  // instead of showing a stale snapshot from whenever the card was clicked.
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const onLoadedPage = assignments.find((a) => a.assignment_id === selectedContactId) ?? null;

  // Pagination is by assignment row, not contact — a contact's "other TM" chip can
  // point at an assignment_id that isn't on the currently loaded page. Fall back to
  // fetching that one assignment directly instead of silently failing to open it.
  const [fallbackContact, setFallbackContact] = useState<HiringContact | null>(null);
  useEffect(() => {
    if (onLoadedPage || selectedContactId == null) {
      setFallbackContact(null);
      return;
    }
    let cancelled = false;
    fetchAssignmentDetail(selectedContactId, isAdmin)
      .then((c) => { if (!cancelled) setFallbackContact(c); })
      .catch(() => { if (!cancelled) setFallbackContact(null); });
    return () => { cancelled = true; };
  }, [selectedContactId, onLoadedPage, isAdmin]);

  const selectedContact = onLoadedPage ?? (fallbackContact?.assignment_id === selectedContactId ? fallbackContact : null);

  // A contact already assigned to one TM can still be assigned to another —
  // this reuses the same "Assign" modal the Unassigned scope uses.
  const [assignTarget, setAssignTarget] = useState<typeof assignments[number] | null>(null);

  // Employees (not admin — they already have "New Contact" in the Unassigned
  // scope) can manually add a profile to track; it's auto-assigned to them.
  const [showCreate, setShowCreate] = useState(false);

  // The backend returns one row per (contact, employee) assignment — when admin
  // views "Everyone", a contact with 2 TMs comes back as 2 rows. Collapse those into
  // a single card per contact; each row already carries the full assigned_employees
  // roster, so the first occurrence has everything the card needs to render all chips.
  const displayedContacts = useMemo(() => {
    const seen = new Set<number>();
    return assignments.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [assignments]);

  useEffect(() => { loadAssignments(1); }, [loadAssignments]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground font-inter shrink-0">Status</span>
        {FILTER_STATUS_OPTIONS.map((o) => {
          const count = o.value === "" ? undefined : statusCounts[o.value];
          return (
            <button
              key={o.value}
              onClick={() => setStatusFilter(o.value)}
              className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-colors flex items-center gap-1.5 ${
                statusFilter === o.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-surface-muted/60 text-muted-foreground border-border hover:border-border hover:text-foreground"
              }`}
            >
              {o.label}
              {count !== undefined && count > 0 && (
                <span className={`text-[10px] font-semibold px-1 py-0.5 rounded min-w-[16px] text-center leading-none ${
                  statusFilter === o.value ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {!isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 hover:border-primary rounded-xl transition-colors font-inter"
          >
            <Plus className="w-4 h-4" />
            New Contact
          </button>
        )}
      </div>

      {assignmentsLoadError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {assignmentsLoadError}
        </div>
      )}

      {assignmentsLoading && assignments.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : assignments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground font-inter">
          <ContactIcon className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">No hiring contacts assigned yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {displayedContacts.map((c) => (
            <ContactCard
              key={c.id}
              contact={c}
              onClick={isAdmin ? undefined : () => setSelectedContactId(c.assignment_id ?? null)}
              onSelectAssignment={isAdmin ? (assignmentId) => setSelectedContactId(assignmentId) : undefined}
              onAssign={isAdmin ? () => setAssignTarget(c) : undefined}
            />
          ))}
        </div>
      )}

      {assignmentsTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadAssignments(assignmentsPage - 1)}
            disabled={assignmentsPage <= 1 || assignmentsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{assignmentsPage} / {assignmentsTotalPages}</span>
          <button
            onClick={() => loadAssignments(assignmentsPage + 1)}
            disabled={assignmentsPage >= assignmentsTotalPages || assignmentsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {selectedContact && (
        <ContactDetailModal
          contact={selectedContact}
          isAdmin={isAdmin}
          onClose={() => setSelectedContactId(null)}
          onManageAssignees={isAdmin ? () => {
            setAssignTarget(selectedContact);
            setSelectedContactId(null);
          } : undefined}
        />
      )}
      {assignTarget && (
        <AssignContactModal contact={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
      {showCreate && (
        <CreateContactModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function UnassignedContactsTab() {
  const {
    contacts, contactsTotalPages, contactsPage, contactsLoading,
    contactsSearchInput, setContactsSearchInput, handleContactsSearchSubmit, loadContacts,
  } = useHiringContacts();

  const [assignTarget, setAssignTarget] = useState<typeof contacts[number] | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadContacts(1); }, [loadContacts]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleContactsSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[200px] max-w-xs">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={contactsSearchInput}
              onChange={(e) => setContactsSearchInput(e.target.value)}
              placeholder="Search name or role…"
              className="w-full bg-surface-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary"
            />
          </div>
        </form>

        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 hover:border-primary rounded-xl transition-colors font-inter"
        >
          <Plus className="w-4 h-4" />
          New Contact
        </button>
      </div>

      {contactsLoading && contacts.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter text-center py-24">No unassigned contacts found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {contacts.map((c) => (
            <ContactCard key={c.id} contact={c} onAssign={() => setAssignTarget(c)} />
          ))}
        </div>
      )}

      {contactsTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadContacts(contactsPage - 1)}
            disabled={contactsPage <= 1 || contactsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{contactsPage} / {contactsTotalPages}</span>
          <button
            onClick={() => loadContacts(contactsPage + 1)}
            disabled={contactsPage >= contactsTotalPages || contactsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {assignTarget && (
        <AssignContactModal contact={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
      {showCreate && (
        <CreateContactModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function ArchivedContactRow({ contact, onRestore, onDelete }: { contact: HiringContact; onRestore: () => void; onDelete: () => void }) {
  return (
    <div className="bg-surface/60 border border-border/60 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full border border-border bg-surface-muted flex items-center justify-center shrink-0 overflow-hidden opacity-60">
        {contact.thumbnail_url ? (
          <img src={contact.thumbnail_url} alt={contact.full_name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-sm font-bold text-primary font-inter">{contact.full_name.charAt(0).toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-foreground font-inter inline-flex items-center gap-1.5">
          {contact.full_name}
          <a href={contact.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-400/70">
            <Linkedin className="w-3.5 h-3.5" />
          </a>
        </h3>
        <p className="text-xs text-muted-foreground font-inter mt-0.5">
          {contact.role || contact.company?.name || ""}
          {contact.archived_at && ` · archived ${new Date(contact.archived_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onRestore}
          className="flex items-center gap-1 text-[11px] text-emerald-300 hover:text-white bg-emerald-600/10 hover:bg-emerald-600 border border-emerald-500/30 hover:border-emerald-500 rounded-lg px-2 py-1 transition-colors font-inter"
        >
          <RotateCcw className="w-3 h-3" />
          Restore
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 text-[11px] text-red-400 hover:text-white bg-red-600/10 hover:bg-red-600 border border-red-500/30 hover:border-red-500 rounded-lg px-2 py-1 transition-colors font-inter"
        >
          <Trash2 className="w-3 h-3" />
          Delete
        </button>
      </div>
    </div>
  );
}

function ArchivedContactsTab() {
  const {
    archivedContacts, archivedContactsTotalPages, archivedContactsPage, archivedContactsLoading,
    loadArchivedContacts, handleRestoreContact, handleDeleteContact,
    contactCleanupModal, contactCleanupExecuting, contactCleanupError,
    openContactCleanup, executeContactCleanup, closeContactCleanupModal,
  } = useHiringContacts();

  const [deleteTarget, setDeleteTarget] = useState<HiringContact | null>(null);
  const [deleteExecuting, setDeleteExecuting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => { loadArchivedContacts(1); }, [loadArchivedContacts]);

  function closeDeleteConfirm() {
    setDeleteTarget(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteExecuting(true);
    setDeleteError(null);
    try {
      await handleDeleteContact(deleteTarget.id);
      closeDeleteConfirm();
    } catch {
      setDeleteError("Failed to delete. Please try again.");
    } finally {
      setDeleteExecuting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => openContactCleanup(180)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:text-destructive-foreground border border-destructive/50 hover:border-destructive hover:bg-destructive rounded-xl transition-colors font-inter"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete 6mo+ Archived
        </button>
        <button
          onClick={() => openContactCleanup(365)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:text-destructive-foreground border border-destructive/50 hover:border-destructive hover:bg-destructive rounded-xl transition-colors font-inter"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete 1yr+ Archived
        </button>
      </div>

      {contactCleanupError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {contactCleanupError}
        </div>
      )}

      {archivedContactsLoading && archivedContacts.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : archivedContacts.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter text-center py-24">No archived contacts.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {archivedContacts.map((c) => (
            <ArchivedContactRow
              key={c.id}
              contact={c}
              onRestore={() => handleRestoreContact(c.id)}
              onDelete={() => setDeleteTarget(c)}
            />
          ))}
        </div>
      )}

      {archivedContactsTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadArchivedContacts(archivedContactsPage - 1)}
            disabled={archivedContactsPage <= 1 || archivedContactsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{archivedContactsPage} / {archivedContactsTotalPages}</span>
          <button
            onClick={() => loadArchivedContacts(archivedContactsPage + 1)}
            disabled={archivedContactsPage >= archivedContactsTotalPages || archivedContactsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Contact"
          message={`Permanently delete "${deleteTarget.full_name}"? This also deletes all assignment history.`}
          confirmLabel="Delete"
          executing={deleteExecuting}
          error={deleteError}
          onConfirm={confirmDelete}
          onClose={closeDeleteConfirm}
        />
      )}

      {contactCleanupModal && (
        <ConfirmModal
          title="Delete Old Archived Contacts"
          message={`Permanently delete ${contactCleanupModal.preview.count} contact${contactCleanupModal.preview.count === 1 ? "" : "s"} archived more than ${contactCleanupModal.days >= 365 ? "1 year" : "6 months"} ago? This also deletes all history.`}
          confirmLabel="Delete"
          executing={contactCleanupExecuting}
          error={contactCleanupError}
          onConfirm={executeContactCleanup}
          onClose={closeContactCleanupModal}
        />
      )}
    </div>
  );
}

/** Merges "Assigned" (per-TM engagement pipeline, CompanyAssignmentsTab) and
 * "Unassigned" (companies nobody is tracking yet, UnassignedCompaniesTab) into
 * one destination with a scope toggle — exact mirror of ContactsMergedTab. */
function CompaniesMergedTab() {
  const [scope, setScope] = useState<"assigned" | "unassigned" | "archived">("assigned");
  const {
    companiesWatchFilter, setCompaniesWatchFilter, assignedCompaniesCount, unassignedCompaniesCount, archivedCompaniesCount,
    companyActionError,
  } = useHiringContacts();

  // Deliberately NOT Object.values(companyStatusCounts).reduce(...) — that sums
  // per-assignment rows, which double-counts a company assigned to >1 TM and
  // breaks the assigned+unassigned=total invariant this tab's two badges promise.
  const assignedCount = assignedCompaniesCount;

  return (
    <div className="space-y-4">
      {companyActionError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {companyActionError}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 p-1 bg-surface/60 border border-border rounded-xl">
          <button
            onClick={() => setScope("assigned")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "assigned" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Assigned
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "assigned" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {assignedCount}
            </span>
          </button>
          <button
            onClick={() => setScope("unassigned")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "unassigned" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Unassigned
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "unassigned" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {unassignedCompaniesCount}
            </span>
          </button>
          <button
            onClick={() => setScope("archived")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-inter rounded-lg transition-colors ${
              scope === "archived" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Archived
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none ${
              scope === "archived" ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
            }`}>
              {archivedCompaniesCount}
            </span>
          </button>
        </div>

        {scope !== "archived" && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Watch:</label>
            <AppSelect value={companiesWatchFilter} onChange={setCompaniesWatchFilter} className="w-36">
              {WATCH_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </AppSelect>
          </div>
        )}
      </div>

      {scope === "assigned" ? <CompanyAssignmentsTab /> : scope === "unassigned" ? <UnassignedCompaniesTab /> : <ArchivedCompaniesTab />}
    </div>
  );
}

/** Per-TM company engagement pipeline — mirrors AssignmentsTab exactly, reused by
 * both admin (inside CompaniesMergedTab's "Assigned" scope, seeing every TM) and
 * by employees (as the top-level "My Companies" tab, scoped to their own rows). */
function CompanyAssignmentsTab() {
  const {
    isAdmin, companyAssignments, companyAssignmentsTotalPages, companyAssignmentsPage,
    companyAssignmentsLoading, companyAssignmentsLoadError, companyStatusFilter, companyStatusCounts,
    setCompanyStatusFilter, loadCompanyAssignments,
  } = useHiringContacts();

  // Store just the id, not the object — deriving it fresh from `companyAssignments` on
  // every render means the open modal picks up watch-toggle/status updates immediately
  // instead of showing a stale snapshot from whenever the card was clicked.
  const [selectedCompanyAssignmentId, setSelectedCompanyAssignmentId] = useState<number | null>(null);
  const onLoadedPage = companyAssignments.find((c) => c.assignment_id === selectedCompanyAssignmentId) ?? null;

  // Pagination is by assignment row, not company — a company's "other TM" chip can
  // point at an assignment_id that isn't on the currently loaded page. Fall back to
  // fetching that one assignment directly instead of silently failing to open it.
  const [fallbackCompany, setFallbackCompany] = useState<Company | null>(null);
  useEffect(() => {
    if (onLoadedPage || selectedCompanyAssignmentId == null) {
      setFallbackCompany(null);
      return;
    }
    let cancelled = false;
    fetchCompanyAssignmentDetail(selectedCompanyAssignmentId, isAdmin)
      .then((c) => { if (!cancelled) setFallbackCompany(c); })
      .catch(() => { if (!cancelled) setFallbackCompany(null); });
    return () => { cancelled = true; };
  }, [selectedCompanyAssignmentId, onLoadedPage, isAdmin]);

  const selectedCompany = onLoadedPage ?? (fallbackCompany?.assignment_id === selectedCompanyAssignmentId ? fallbackCompany : null);

  // A company already assigned to one TM can still be assigned to another —
  // this reuses the same "Assign" modal the Browse scope uses.
  const [assignTarget, setAssignTarget] = useState<typeof companyAssignments[number] | null>(null);

  // Employees (not admin — they already have "New Company" in the Browse scope,
  // if admin) can manually add a company to track; it's auto-assigned to them.
  const [showCreate, setShowCreate] = useState(false);

  // The backend returns one row per (company, employee) assignment — when admin
  // views "Everyone", a company with 2 TMs comes back as 2 rows. Collapse those into
  // a single card per company; each row already carries the full assigned_employees
  // roster, so the first occurrence has everything the card needs to render all chips.
  const displayedCompanies = useMemo(() => {
    const seen = new Set<number>();
    return companyAssignments.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [companyAssignments]);

  useEffect(() => { loadCompanyAssignments(1); }, [loadCompanyAssignments]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground font-inter shrink-0">Status</span>
        {FILTER_COMPANY_STATUS_OPTIONS.map((o) => {
          const count = o.value === "" ? undefined : companyStatusCounts[o.value];
          return (
            <button
              key={o.value}
              onClick={() => setCompanyStatusFilter(o.value)}
              className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter font-medium transition-colors flex items-center gap-1.5 ${
                companyStatusFilter === o.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-surface-muted/60 text-muted-foreground border-border hover:border-border hover:text-foreground"
              }`}
            >
              {o.label}
              {count !== undefined && count > 0 && (
                <span className={`text-[10px] font-semibold px-1 py-0.5 rounded min-w-[16px] text-center leading-none ${
                  companyStatusFilter === o.value ? "bg-white/20 text-current" : "bg-muted text-muted-foreground"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}

        {!isAdmin && (
          <button
            onClick={() => setShowCreate(true)}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 hover:border-primary rounded-xl transition-colors font-inter"
          >
            <Plus className="w-4 h-4" />
            New Company
          </button>
        )}
      </div>

      {companyAssignmentsLoadError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {companyAssignmentsLoadError}
        </div>
      )}

      {companyAssignmentsLoading && companyAssignments.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : companyAssignments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground font-inter">
          <Building2 className="w-10 h-10 mb-3 opacity-40" />
          <p className="text-sm">No companies assigned yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {displayedCompanies.map((c) => (
            <CompanyAssignmentCard
              key={c.id}
              company={c}
              onClick={isAdmin ? undefined : () => setSelectedCompanyAssignmentId(c.assignment_id ?? null)}
              onSelectAssignment={isAdmin ? (assignmentId) => setSelectedCompanyAssignmentId(assignmentId) : undefined}
              onAssign={isAdmin ? () => setAssignTarget(c) : undefined}
            />
          ))}
        </div>
      )}

      {companyAssignmentsTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadCompanyAssignments(companyAssignmentsPage - 1)}
            disabled={companyAssignmentsPage <= 1 || companyAssignmentsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{companyAssignmentsPage} / {companyAssignmentsTotalPages}</span>
          <button
            onClick={() => loadCompanyAssignments(companyAssignmentsPage + 1)}
            disabled={companyAssignmentsPage >= companyAssignmentsTotalPages || companyAssignmentsLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {selectedCompany && (
        <CompanyAssignmentDetailModal
          company={selectedCompany}
          isAdmin={isAdmin}
          onClose={() => setSelectedCompanyAssignmentId(null)}
          onManageAssignees={isAdmin ? () => {
            setAssignTarget(selectedCompany);
            setSelectedCompanyAssignmentId(null);
          } : undefined}
        />
      )}
      {assignTarget && (
        <AssignCompanyModal company={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
      {showCreate && (
        <CreateCompanyModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function UnassignedCompaniesTab() {
  const {
    companies, companiesTotalPages, companiesPage, companiesLoading, companiesSearchInput,
    setCompaniesSearchInput, handleCompaniesSearchSubmit, loadCompanies,
  } = useHiringContacts();

  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { loadCompanies(1); }, [loadCompanies]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <form onSubmit={handleCompaniesSearchSubmit} className="flex items-center gap-2 max-w-xs flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={companiesSearchInput}
              onChange={(e) => setCompaniesSearchInput(e.target.value)}
              placeholder="Search name or domain…"
              className="w-full bg-surface-muted border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary"
            />
          </div>
        </form>

        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-sm text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 hover:border-primary rounded-xl transition-colors font-inter"
        >
          <Plus className="w-4 h-4" />
          New Company
        </button>
      </div>

      {companiesLoading && companies.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <CompanyTable companies={companies} />
      )}

      {companiesTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadCompanies(companiesPage - 1)}
            disabled={companiesPage <= 1 || companiesLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{companiesPage} / {companiesTotalPages}</span>
          <button
            onClick={() => loadCompanies(companiesPage + 1)}
            disabled={companiesPage >= companiesTotalPages || companiesLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {showCreate && (
        <CreateCompanyModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function ArchivedCompanyRow({ company, onRestore, onDelete }: { company: Company; onRestore: () => void; onDelete: () => void }) {
  return (
    <div className="bg-surface/60 border border-border/60 rounded-xl p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center shrink-0 opacity-60">
        <span className="text-sm font-bold text-primary font-inter">{company.name.charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-foreground font-inter inline-flex items-center gap-1.5">
          {company.name}
          {company.linkedin_url && (
            <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-400/70">
              <Linkedin className="w-3.5 h-3.5" />
            </a>
          )}
        </h3>
        <p className="text-xs text-muted-foreground font-inter mt-0.5">
          {company.domain}
          {company.archived_at && ` · archived ${new Date(company.archived_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onRestore}
          className="flex items-center gap-1 text-[11px] text-emerald-300 hover:text-white bg-emerald-600/10 hover:bg-emerald-600 border border-emerald-500/30 hover:border-emerald-500 rounded-lg px-2 py-1 transition-colors font-inter"
        >
          <RotateCcw className="w-3 h-3" />
          Restore
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-1 text-[11px] text-red-400 hover:text-white bg-red-600/10 hover:bg-red-600 border border-red-500/30 hover:border-red-500 rounded-lg px-2 py-1 transition-colors font-inter"
        >
          <Trash2 className="w-3 h-3" />
          Delete
        </button>
      </div>
    </div>
  );
}

function ArchivedCompaniesTab() {
  const {
    archivedCompanies, archivedCompaniesTotalPages, archivedCompaniesPage, archivedCompaniesLoading,
    loadArchivedCompanies, handleRestoreCompany, handleDeleteCompany,
    companyCleanupModal, companyCleanupExecuting, companyCleanupError,
    openCompanyCleanup, executeCompanyCleanup, closeCompanyCleanupModal,
  } = useHiringContacts();

  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [deleteContactCount, setDeleteContactCount] = useState<number | null>(null);
  const [deleteExecuting, setDeleteExecuting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Guards against a stale response landing after the admin has already moved
  // on to a different row (or closed the modal) — without this, two rapid
  // Delete clicks on different companies could show the wrong contact count.
  const deleteRequestId = useRef(0);

  useEffect(() => { loadArchivedCompanies(1); }, [loadArchivedCompanies]);

  async function openDeleteConfirm(company: Company) {
    const requestId = ++deleteRequestId.current;
    setDeleteTarget(company);
    setDeleteContactCount(null);
    setDeleteError(null);
    try {
      // ON DELETE CASCADE removes every contact under this company regardless of
      // its own active/archived state — sum both so the confirm dialog shows the
      // true impact, not just the archived ones.
      const [activeRes, archivedRes] = await Promise.all([
        fetchContacts({ company_id: company.id, page_size: 1, is_active: true }),
        fetchContacts({ company_id: company.id, page_size: 1, is_active: false }),
      ]);
      if (requestId !== deleteRequestId.current) return;
      setDeleteContactCount(activeRes.total_count + archivedRes.total_count);
    } catch {
      if (requestId !== deleteRequestId.current) return;
      setDeleteContactCount(null);
    }
  }

  function closeDeleteConfirm() {
    deleteRequestId.current++; // invalidate any in-flight count fetch
    setDeleteTarget(null);
    setDeleteContactCount(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteExecuting(true);
    setDeleteError(null);
    try {
      await handleDeleteCompany(deleteTarget.id);
      closeDeleteConfirm();
    } catch {
      setDeleteError("Failed to delete. Please try again.");
    } finally {
      setDeleteExecuting(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground font-inter">
          Archiving a company also archives every contact under it. Restoring a company does not restore its contacts.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => openCompanyCleanup(180)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:text-destructive-foreground border border-destructive/50 hover:border-destructive hover:bg-destructive rounded-xl transition-colors font-inter"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete 6mo+ Archived
          </button>
          <button
            onClick={() => openCompanyCleanup(365)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive hover:text-destructive-foreground border border-destructive/50 hover:border-destructive hover:bg-destructive rounded-xl transition-colors font-inter"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete 1yr+ Archived
          </button>
        </div>
      </div>

      {companyCleanupError && (
        <div className="flex items-center gap-2 text-sm text-red-400 font-inter bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          {companyCleanupError}
        </div>
      )}

      {archivedCompaniesLoading && archivedCompanies.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground font-inter text-sm">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : archivedCompanies.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter text-center py-24">No archived companies.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {archivedCompanies.map((c) => (
            <ArchivedCompanyRow
              key={c.id}
              company={c}
              onRestore={() => handleRestoreCompany(c.id)}
              onDelete={() => openDeleteConfirm(c)}
            />
          ))}
        </div>
      )}

      {archivedCompaniesTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => loadArchivedCompanies(archivedCompaniesPage - 1)}
            disabled={archivedCompaniesPage <= 1 || archivedCompaniesLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground font-inter">{archivedCompaniesPage} / {archivedCompaniesTotalPages}</span>
          <button
            onClick={() => loadArchivedCompanies(archivedCompaniesPage + 1)}
            disabled={archivedCompaniesPage >= archivedCompaniesTotalPages || archivedCompaniesLoading}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-lg transition-colors disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Company"
          message={
            deleteContactCount === null
              ? `Permanently delete "${deleteTarget.name}"? This also deletes every contact under it and all assignment history.`
              : `Permanently delete "${deleteTarget.name}"? This also permanently deletes ${deleteContactCount} contact${deleteContactCount === 1 ? "" : "s"} under it and all assignment history.`
          }
          confirmLabel="Delete"
          executing={deleteExecuting}
          error={deleteError}
          onConfirm={confirmDelete}
          onClose={closeDeleteConfirm}
        />
      )}

      {companyCleanupModal && (
        <ConfirmModal
          title="Delete Old Archived Companies"
          message={`Permanently delete ${companyCleanupModal.preview.count} compan${companyCleanupModal.preview.count === 1 ? "y" : "ies"} archived more than ${companyCleanupModal.days >= 365 ? "1 year" : "6 months"} ago? This also deletes their contacts and all history.`}
          confirmLabel="Delete"
          executing={companyCleanupExecuting}
          error={companyCleanupError}
          onConfirm={executeCompanyCleanup}
          onClose={closeCompanyCleanupModal}
        />
      )}
    </div>
  );
}
